import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, Typography, CircularProgress, Dialog } from '@mui/material';
import theme from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DayNavigationProvider } from './contexts/DayNavigationContext';
import { TutorialProvider } from './components/TutorialSystem';
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { GlobalLayout } from './components/GlobalLayout';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { persistentCache } from './services/persistentCache';
import { DataDisclosureDialog } from './components/DataDisclosureDialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NotificationPrompt, useNotificationPrompt } from './components/NotificationPrompt';
import { ReviewPrompt, useReviewPrompt } from './components/ReviewPrompt';
import { StandardButton } from './components/design-system';
import { API_BASE_URL } from './services/api';
import { captureAttribution } from './utils/attribution';
import { isStoreApp } from './utils/platform';
import './styles/global.css';

// Google OAuth - conditionally import
let GoogleOAuthProvider: React.FC<{ clientId: string; children: React.ReactNode }> | null = null;
try {
  const googleOAuth = require('@react-oauth/google');
  GoogleOAuthProvider = googleOAuth.GoogleOAuthProvider;
} catch {
  // @react-oauth/google not installed
}

// Lazy load the page components
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/Login'));
const RegisterPage = lazy(() => import('./pages/Register'));
const importDashboard = () => import('./pages/Dashboard');
const Dashboard = lazy(importDashboard);
// Returning users land on the Dashboard — start pulling its code while React
// is still booting rather than after the first render asks for it.
if (localStorage.getItem('token')) {
  importDashboard().catch(() => {});
}
const FoodLog = lazy(() => import('./pages/FoodLog'));
const MyFoodsPage = lazy(() => import('./pages/MyFoods'));
const SupplementsSchedulePage = lazy(() => import('./pages/SupplementsNew'));
const BloodworkPage = lazy(() => import('./pages/Bloodwork'));
const AnalysisPage = lazy(() => import('./pages/Analysis'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const UpgradePage = lazy(() => import('./pages/Upgrade'));
const PrivacyPage = lazy(() => import('./pages/Privacy'));
const TermsPage = lazy(() => import('./pages/Terms'));
const AdminReports = lazy(() => import('./pages/AdminReports').then(module => ({ default: module.AdminReports })));
const Admin = lazy(() => import('./pages/Admin'));
const SocialPage = lazy(() => import('./pages/Social').then(module => ({ default: module.Social })));
const FriendsPage = lazy(() => import('./pages/Friends').then(module => ({ default: module.Social })));
const CoachPage = lazy(() => import('./pages/Coach'));
const GarminPage = lazy(() => import('./pages/Garmin'));
const ExerciseLog = lazy(() => import('./pages/ExerciseLog'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'));
const OnboardingPage = lazy(() => import('./pages/Onboarding'));
const importScanPage = () => import('./components/scan/ScanPage');
const ScanPage = lazy(importScanPage);
const ActivationScreen = lazy(() => import('./pages/ActivationScreen'));

// Redirects /verify-email?token=xxx to the backend verification endpoint
const VerifyEmailRedirect: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (token) {
    window.location.href = `${API_BASE_URL}/auth/verify/${token}`;
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  }
  return <Navigate to="/login" replace />;
};

// Sends new users (account < 3 days old, not yet graduated from the activation
// checklist) to the onboarding activation screen; everyone else to the
// dashboard. Synchronous so it never blocks routing on a network call.
const HomeRedirect: React.FC = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const graduated = localStorage.getItem(`fueltron_activation_graduated_${user._id}`) === 'true';
  const createdAtMs = user.createdAt ? new Date(user.createdAt).getTime() : 0;
  const isNew = createdAtMs > 0 && (Date.now() - createdAtMs) < 3 * 24 * 60 * 60 * 1000;
  return <Navigate to={!graduated && isNew ? '/activation' : '/dashboard'} replace />;
};

// First-touch source (utm / referrer) is read before anything else renders.
captureAttribution();

const AppRoutes: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [showDataDisclosure, setShowDataDisclosure] = useState(false);
  const [hasAcceptedDisclosure, setHasAcceptedDisclosure] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const { showPrompt, setShowPrompt, handleEnable } = useNotificationPrompt();
  const { showReviewPrompt, setShowReviewPrompt } = useReviewPrompt();

  // Sweep stale/expired persistent-cache entries once at startup so the cache
  // can't slowly fill the localStorage quota for daily users (which would make
  // set() silently fail and break the cache-first render).
  useEffect(() => {
    persistentCache.sweepExpired();
  }, []);

  // Snapping a meal photo is the most time-sensitive thing a user does, so warm
  // the scan screen's code shortly after sign-in — tapping the camera then
  // opens it instantly instead of showing a loading spinner first.
  const isSignedIn = !!auth.user;
  useEffect(() => {
    if (!isSignedIn) return;
    const timer = setTimeout(() => { importScanPage().catch(() => {}); }, 1500);
    return () => clearTimeout(timer);
  }, [isSignedIn]);

  // Check if user has already accepted the data disclosure
  useEffect(() => {
    if (auth.user && !hasAcceptedDisclosure) {
      // Use user-specific key so disclosure shows for each new account
      const userDisclosureKey = `fuelTron_data_disclosure_${auth.user._id}`;
      const disclosureAccepted = localStorage.getItem(userDisclosureKey);
      if (!disclosureAccepted) {
        setShowDataDisclosure(true);
      } else {
        setHasAcceptedDisclosure(true);
        // Trigger AI welcome for new users who already accepted disclosure
        if (localStorage.getItem('firstTimeUser') === 'yes' && !localStorage.getItem('aiWelcomeShown')) {
          setTimeout(() => {
            window.dispatchEvent(new Event('disclosureAccepted'));
          }, 500);
        }
      }
    }
  }, [auth.user, hasAcceptedDisclosure]);

  const handleAcceptDisclosure = () => {
    if (auth.user?._id) {
      localStorage.setItem(`fuelTron_data_disclosure_${auth.user._id}`, 'true');
    }
    setHasAcceptedDisclosure(true);
    setShowDataDisclosure(false);

    // Notify AIBarContext to show welcome for new users
    // Small delay to ensure the disclosure dialog has closed and UI has settled
    setTimeout(() => {
      window.dispatchEvent(new Event('disclosureAccepted'));
    }, 500);
  };

  const handleDeclineDisclosure = () => {
    // If user declines, we should log them out and redirect to landing
    auth.logout();
    setShowDataDisclosure(false);
  };
  
  return (
    <>
      <Suspense fallback={
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      }>
        <Routes>
          {/* Public routes */}
          {/* A store install (iOS app or Play Store app) already came through a store listing —
              the landing page would be a second pitch, so it opens on the demo instead. */}
          <Route path="/" element={auth.user ? <HomeRedirect /> : auth.loading ? <Box sx={{ minHeight: '100vh' }} /> : isStoreApp() ? <Navigate to="/onboarding" replace /> : <LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/home" element={<LandingPage />} />
          {/* While a saved session is still loading, render nothing: showing onboarding for that
              split second recorded existing users as visitors who left at the demo (2026-10-05). */}
          <Route path="/onboarding" element={auth.user ? <HomeRedirect /> : auth.loading ? <Box sx={{ minHeight: '100vh' }} /> : <OnboardingPage />} />
          <Route path="/login" element={!auth.user ? <LoginPage /> : <HomeRedirect />} />
          <Route path="/register" element={!auth.user ? <RegisterPage /> : <HomeRedirect />} />
          <Route path="/signup" element={!auth.user ? <RegisterPage /> : <HomeRedirect />} />
          <Route path="/verify-email" element={<VerifyEmailRedirect />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          
          {/* Protected routes */}
          <Route path="/*" element={<ProtectedRoute><NotificationsProvider><GlobalLayout /></NotificationsProvider></ProtectedRoute>}>
            <Route index element={<HomeRedirect />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="activation" element={<ActivationScreen />} />
            <Route path="food-log" element={<FoodLog />} />
            <Route path="my-foods" element={<MyFoodsPage />} />
            <Route path="supplements" element={<SupplementsSchedulePage />} />
            <Route path="bloodwork" element={<BloodworkPage />} />
            <Route path="analysis" element={<AnalysisPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="friends" element={<FriendsPage />} />
            <Route path="social" element={<Navigate to="/friends" replace />} />
            <Route path="coach" element={<CoachPage />} />
            <Route path="challenges" element={<Navigate to="/coach" replace />} />
            <Route path="exercise-log" element={<ExerciseLog />} />
            <Route path="scan" element={<ScanPage />} />
            <Route path="fitness" element={<GarminPage />} />
            <Route path="demo" element={
              <Box sx={{ p: 3 }}>
                <Typography variant="h4">Demo Page</Typography>
                <Typography>This is a placeholder page for future features.</Typography>
              </Box>
            } />
            <Route path="upgrade" element={<UpgradePage />} />
            <Route path="admin-reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
            <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} />
          </Route>
        </Routes>
      </Suspense>

      {/* Data Disclosure Dialog */}
      <DataDisclosureDialog
        open={showDataDisclosure}
        onAccept={handleAcceptDisclosure}
        onDecline={handleDeclineDisclosure}
      />

      {/* Notification Prompt */}
      {showPrompt && (
        <NotificationPrompt
          onClose={() => setShowPrompt(false)}
          onEnable={handleEnable}
        />
      )}

      {/* Store-review ask — only after the notification ask has had its turn */}
      {showReviewPrompt && !showPrompt && (
        <ReviewPrompt onClose={() => setShowReviewPrompt(false)} />
      )}
    </>
  );
};

const GoogleWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (GoogleOAuthProvider && clientId) {
    return <GoogleOAuthProvider clientId={clientId}>{children}</GoogleOAuthProvider>;
  }
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <CustomThemeProvider>
          <GoogleWrapper>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AuthProvider>
                <DayNavigationProvider>
                  <TutorialProvider>
                    <AppRoutes />
                  </TutorialProvider>
                </DayNavigationProvider>
              </AuthProvider>
            </Router>
          </GoogleWrapper>
        </CustomThemeProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
